import { merge, from, concat, EMPTY, Observable, of } from 'rxjs';
import {
  map,
  concatMap,
  take,
  filter,
  distinctUntilChanged,
  share,
  ignoreElements,
  finalize,
  mergeMapTo,
} from 'rxjs/operators';
import { BirdLayoutState } from '@sas-dvr/internal-va-react-core/store/birdLayout/BirdLayoutState';
import {
  TableElementController,
  VisualController,
  PositionedModelOwnerController,
  BIRDModelController,
  PositionedModelController,
  VisualElementController,
} from '@sas-dvr/ltjs-bird';
import { AnyAction } from 'redux';
import {
  MaximizeData,
  MaximizeState,
} from '@sas-dvr/internal-va-react-core/store/maximize/maximizeState';
import { Mutable } from '@sas-dvr/internal-va-react-core/utility-types';
import { filterNulls } from './arrayUtils';
import { StateObservable } from 'redux-observable';
import { ReportState } from '@sas-dvr/internal-va-react-core/store/rootReducer';
import {
  selectLayoutState,
  selectMaximizeState,
} from '@sas-dvr/internal-va-react-core/store/selectors';
import { EpicActions } from '../actions/epicActions';
import { ReportContextMenuState } from '@sas-dvr/internal-va-react-core/store/reportContextMenu/ReportContextMenuState';
import { BirdState } from '@sas-dvr/internal-va-react-core/store/bird/birdState';
import { ContextMenuItems } from './contextMenu';

export function getMaximizeObservables(
  id: string,
  visualController: PositionedModelController | null,
  visualElementController: VisualElementController,
  state$: StateObservable<ReportState>,
  unload$: Observable<BIRDModelController>,
  options?: { preferOverflowMenu?: boolean }
) {
  if (
    !visualController ||
    (visualController instanceof VisualController &&
      visualController.getCompositeParent() !== null) ||
    // not supported for objects
    state$.value.bird.name
  ) {
    return EMPTY;
  }

  const supplementalControllers = filterNulls(
    visualElementController.getSupplementalVisualElementControllers()
  );

  // Load supplemental controllers once upfront.
  // Loading and unloading them while maximizing caused graphs to lose their selection state.
  const loadActions: AnyAction[] = [];
  const unloadActions: AnyAction[] = [];
  for (const controller of supplementalControllers) {
    loadActions.push(EpicActions.actions.loadBirdModel(controller));
    unloadActions.push(EpicActions.actions.unloadBirdModel(controller));
  }

  const isMaximized$ = state$.pipe(
    map((state) => state.bird.maximizedVisual === id),
    distinctUntilChanged(),
    share()
  );
  const maximized$ = isMaximized$.pipe(filter((value) => value));
  const restored$ = isMaximized$.pipe(filter((value) => !value));
  return merge(
    from(loadActions),
    unload$.pipe(mergeMapTo(from(unloadActions))),
    of(
      MaximizeState.actions.set(id, {
        isMaximized: false,
        preferOverflowMenu: options?.preferOverflowMenu ?? false,
        supplementalKeys: filterNulls(
          supplementalControllers.map((controller) => controller.getVisualElementName() ?? null)
        ),
      })
    ),
    // Keep MaximizeData.isMaximized updated
    isMaximized$.pipe(map((isMaximized) => MaximizeState.actions.update(id, { isMaximized }))),
    // Keep ReportContextMenuState.maximizeTarget updated
    state$.pipe(
      map((state) => state.ui.reportContextMenu.target?.id),
      distinctUntilChanged(),
      filter(
        (menuTargetId) =>
          !!menuTargetId &&
          (menuTargetId === id ||
            selectLayoutState(state$.value, menuTargetId).compositeParentKey === id)
      ),
      map(() => {
        const maximizeState = selectMaximizeState(state$.value, id);
        return ReportContextMenuState.actions.setObjectItem(
          ContextMenuItems.MAXIMIZE,
          maximizeState
            ? {
                text: maximizeState.isMaximized
                  ? ({ key: 'ExpandControl.button.restore.txt' } as const)
                  : ({ key: 'ExpandControl.button.expand.txt' } as const),
                action: BirdState.actions.toggleMaximizedVisual(id),
              }
            : undefined
        );
      })
    ),
    // Update store and portable when maximized
    maximized$.pipe(
      concatMap(() => {
        const maximizeActions: AnyAction[] = [];
        const restoreActions: AnyAction[] = [];
        const supplementalControllers = filterNulls(
          visualElementController.getSupplementalVisualElementControllers()
        );
        const supplementalVisualLabels: Mutable<MaximizeData['supplementalVisualLabels']> = {};
        for (const controller of supplementalControllers) {
          const name = controller.getVisualElementName();
          if (!name || !(controller instanceof TableElementController)) {
            continue;
          }
          supplementalVisualLabels[name] = controller.getPresentationLabel() ?? undefined;
        }
        maximizeActions.push(MaximizeState.actions.update(id, { supplementalVisualLabels }));

        // Set up containers as a new layout root
        if (visualController instanceof PositionedModelOwnerController) {
          const mediaKey = 'media-maximized';
          maximizeActions.push(
            MaximizeState.actions.update(id, { mediaKey }),
            EpicActions.actions.addLayoutRoot(visualController, mediaKey),
            BirdLayoutState.actions.setChildKeys(mediaKey, [id])
          );
          restoreActions.push(
            MaximizeState.actions.update(id, { mediaKey: undefined }),
            EpicActions.actions.removeLayoutRoot(visualController),
            BirdLayoutState.actions.set(mediaKey, {})
          );
        }
        return concat(
          from(maximizeActions).pipe(finalize(() => visualController.setMaximized(true))),
          merge(restored$, unload$).pipe(take(1), ignoreElements()), // wait for restore or parent unload before unloading
          from(restoreActions).pipe(finalize(() => visualController.setMaximized(false)))
        );
      })
    )
  );
}
